import React, { useState, useEffect } from 'react';
import { buildSitePageInfoUrl, getSitePageInfoHeaders } from './config';

interface FAQItem {
  id: number;
  infoName: string;
  infoHeader: string;
  inforDetails: string;
  infoType: string;
  tags: string;
  mainimage: string;
  createdDate: string;
}

const FAQ: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFaq, setSelectedFaq] = useState<FAQItem | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      
      // Try the direct API call first
      try {
        const response = await fetch(buildSitePageInfoUrl('faq'), {
          method: 'GET',
          headers: getSitePageInfoHeaders(),
          mode: 'cors'
        });
        
        if (response.ok) {
          const data = await response.json();
          setFaqs(data);
          return;
        }
      } catch (corsError) {
        console.log('CORS error, trying alternative approach:', corsError);
      }
      
      // Fallback: Use mock data if API is not accessible
      const mockData = [
        {
          id: 1,
          infoName: "Welcome to Our Knowledge Base",
          infoHeader: "Getting started with our platform",
          inforDetails: "This is a sample article to demonstrate the knowledge base functionality. In a real scenario, this would be populated from your API.",
          infoType: "Getting Started",
          tags: "welcome,intro,getting-started",
          mainimage: "",
          createdDate: new Date().toISOString()
        },
        {
          id: 2,
          infoName: "CORS Configuration Guide",
          infoHeader: "How to configure CORS for your API",
          inforDetails: "Cross-Origin Resource Sharing (CORS) is a security feature implemented by web browsers. To fix CORS issues, you need to configure your API server to allow requests from your frontend domain.",
          infoType: "Technical",
          tags: "cors,api,configuration",
          mainimage: "",
          createdDate: new Date().toISOString()
        }
      ];
      
      setFaqs(mockData);
      setError("Using sample data - API connection failed due to CORS policy. Please configure CORS on your API server.");
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = ['All', ...Array.from(new Set(faqs.map(faq => faq.infoType)))];

  // Filter FAQs based on category and search term
  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'All' || faq.infoType === selectedCategory;
    const matchesSearch = faq.infoName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.infoHeader.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.inforDetails.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Strip HTML tags from content
  const stripHtml = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  // Truncate text
  const truncateText = (text: string, maxLength: number = 200) => {
    const stripped = stripHtml(text);
    return stripped.length > maxLength ? stripped.substring(0, maxLength) + '...' : stripped;
  };

  // Handle card click to show full details
  const handleCardClick = (faq: FAQItem) => {
    setSelectedFaq(faq);
    setShowDetails(true);
  };

  // Close details view and go back to grid
  const goBackToGrid = () => {
    setShowDetails(false);
    setSelectedFaq(null);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          {/* Loading Spinner */}
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2c5aa0',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          
          {/* Loading Text */}
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            Loading Knowledge Base...
          </div>
          
          {/* Animated Dots */}
          <div style={{
            fontSize: '1.5rem',
            letterSpacing: '2px',
            animation: 'dots 1.5s infinite'
          }}>
            • • •
          </div>
        </div>
        
        {/* CSS Animation Styles */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes dots {
            0%, 20% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'left', 
        padding: '40px',
        color: '#d32f2f',
        fontSize: '1.1rem'
      }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ padding: window.innerWidth < 768 ? '20px 16px' : '40px 20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ 
        textAlign: 'left', 
        marginBottom: window.innerWidth < 768 ? '24px' : '40px', 
        color: '#333',
        fontSize: window.innerWidth < 480 ? '1.5rem' : window.innerWidth < 768 ? '2rem' : '2.5rem',
        fontWeight: 'bold'
      }}>
        Knowledge Base
      </h1>
      
      {showDetails && selectedFaq ? (
        // Detailed Article View
        <div>
          {/* Back Button */}
          <button
            onClick={goBackToGrid}
            style={{
              marginBottom: window.innerWidth < 768 ? '20px' : '30px',
              padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
              backgroundColor: '#2c5aa0',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1e3d72';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2c5aa0';
            }}
          >
            ← Back to Articles
          </button>

          {/* Article Content */}
          <article style={{ 
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0'
          }}>
            {/* Title */}
            <h1 style={{
              color: '#2c5aa0',
              marginBottom: '20px',
              fontSize: '2.5rem',
              fontWeight: '700',
              lineHeight: '1.2',
              textAlign: 'left'
            }}>
              {selectedFaq.infoName}
            </h1>

            {/* Header/Subtitle */}
            <h2 style={{
              color: '#444',
              marginBottom: '32px',
              fontSize: '1.4rem',
              fontWeight: '500',
              lineHeight: '1.4',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '16px',
              textAlign: 'left'
            }}>
              {selectedFaq.infoHeader}
            </h2>

            {/* Main Image */}
            {selectedFaq.mainimage && (
              <div style={{ marginBottom: '32px' }}>
                <img 
                  src={selectedFaq.mainimage}
                  alt={selectedFaq.infoName}
                  style={{
                    width: '100%',
                    maxHeight: '400px',
                    objectFit: 'cover',
                    borderRadius: '12px'
                  }}
                />
              </div>
            )}

            {/* Full Content */}
            <div
              className="faq-content"
              style={{
                color: '#333',
                lineHeight: '1.8',
                fontSize: '1.1rem',
                marginBottom: '40px'
              }}
              dangerouslySetInnerHTML={{ __html: selectedFaq.inforDetails }}
            />

            {/* Inline styles for content formatting */}
            <style>{`
              .faq-content ul {
                margin: 16px 0 !important;
                padding-left: 24px !important;
                list-style-type: disc !important;
              }
              
              .faq-content ul ul {
                margin: 8px 0 !important;
                padding-left: 20px !important;
                list-style-type: circle !important;
              }
              
              .faq-content ol {
                margin: 16px 0 !important;
                padding-left: 24px !important;
                list-style-type: decimal !important;
              }
              
              .faq-content li {
                margin: 8px 0 !important;
                padding-left: 4px !important;
                line-height: 1.6 !important;
              }
              
              .faq-content li p {
                margin: 4px 0 !important;
              }

              .faq-content dl {
                margin: 20px 0 !important;
                padding: 16px !important;
                background-color: #f8f9fa !important;
                border-radius: 8px !important;
                border-left: 4px solid #2c5aa0 !important;
              }

              .faq-content dt {
                font-weight: 600 !important;
                color: #2c5aa0 !important;
                margin: 12px 0 4px 0 !important;
                font-size: 1rem !important;
                display: inline-block !important;
                min-width: 120px !important;
              }

              .faq-content dd {
                margin: 0 0 12px 0 !important;
                padding-left: 8px !important;
                color: #333 !important;
                font-size: 1rem !important;
                display: inline-block !important;
                margin-left: 8px !important;
              }

              .faq-content dt:first-of-type {
                margin-top: 0 !important;
              }

              .faq-content dd:last-of-type {
                margin-bottom: 0 !important;
              }

              .faq-content h1, .faq-content h2, .faq-content h3, 
              .faq-content h4, .faq-content h5, .faq-content h6 {
                margin: 24px 0 16px 0 !important;
                font-weight: 600 !important;
                color: #2c5aa0 !important;
              }

              .faq-content p {
                margin: 12px 0 !important;
                line-height: 1.7 !important;
              }

              .faq-content blockquote {
                margin: 16px 0 !important;
                padding: 16px 20px !important;
                border-left: 4px solid #2c5aa0 !important;
                background-color: #f8f9fa !important;
                font-style: italic !important;
              }

              .faq-content code {
                background-color: #f1f1f1 !important;
                padding: 2px 6px !important;
                border-radius: 4px !important;
                font-family: 'Courier New', monospace !important;
                font-size: 0.9em !important;
              }

              .faq-content pre {
                background-color: #f8f9fa !important;
                padding: 16px !important;
                border-radius: 8px !important;
                overflow-x: auto !important;
                margin: 16px 0 !important;
              }

              .faq-content a {
                color: #2c5aa0 !important;
                text-decoration: underline !important;
              }

              .faq-content a:hover {
                color: #1e3d72 !important;
              }

              .faq-content strong, .faq-content b {
                font-weight: 600 !important;
              }

              .faq-content em, .faq-content i {
                font-style: italic !important;
              }

              .faq-content table {
                width: 100% !important;
                border-collapse: collapse !important;
                margin: 16px 0 !important;
                background-color: #fff !important;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
              }

              .faq-content th, .faq-content td {
                padding: 12px !important;
                text-align: left !important;
                border-bottom: 1px solid #e0e0e0 !important;
              }

              .faq-content th {
                background-color: #f8f9fa !important;
                font-weight: 600 !important;
                color: #2c5aa0 !important;
              }

              .faq-content tr:hover {
                background-color: #f8f9fa !important;
              }
            `}</style>

            {/* Tags Section */}
            {selectedFaq.tags && (
              <div style={{ 
                marginBottom: '32px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <h3 style={{ 
                  color: '#666', 
                  marginBottom: '16px', 
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  Related Topics:
                </h3>
                <div>
                  {selectedFaq.tags.split(',').map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#2c5aa0',
                        color: '#fff',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        marginRight: '12px',
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}
                    >
                      #{tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Article Meta Information */}
            <div style={{
              borderTop: '2px solid #e0e0e0',
              paddingTop: '24px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              color: '#666',
              fontSize: '1rem'
            }}>
              <div>
                <strong>Published Date:</strong><br />
                {new Date(selectedFaq.createdDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div>
                <strong>Article ID:</strong><br />
                #{selectedFaq.id}
              </div>
              <div>
                <strong>Category:</strong><br />
                {selectedFaq.infoType}
              </div>
            </div>
          </article>
        </div>
      ) : (
        // Grid View
        <>
          {/* Search and Filter Section */}
          <div style={{ 
            marginBottom: '30px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '20px',
            alignItems: 'flex-start'
          }}>
            {/* Search Bar */}
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '12px 16px',
                fontSize: '1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '25px',
                width: '100%',
                maxWidth: '400px',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#2c5aa0'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* View Mode Toggle */}
          <div style={{ 
            marginBottom: '20px', 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '10px'
          }}>
            <button 
              onClick={() => setViewMode('grid')}
              style={{
                padding: '8px 16px',
                border: viewMode === 'grid' ? '2px solid #2c5aa0' : '2px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: viewMode === 'grid' ? '#2c5aa0' : '#fff',
                color: viewMode === 'grid' ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}
            >
              📱 Grid View
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                border: viewMode === 'list' ? '2px solid #2c5aa0' : '2px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: viewMode === 'list' ? '#2c5aa0' : '#fff',
                color: viewMode === 'list' ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}
            >
              📋 List View
            </button>
          </div>

          {/* Results Count */}
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'left', 
            color: '#666',
            fontSize: '1rem'
          }}>
            Showing {filteredFAQs.length} article{filteredFAQs.length !== 1 ? 's' : ''}
          </div>
          
          {/* FAQ Items - Grid or List View */}
          {viewMode === 'grid' ? (
            // Grid View
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
              gap: '25px' 
            }}>
              {filteredFAQs.map((faq) => (
                <div 
                  key={faq.id}
                  onClick={() => handleCardClick(faq)}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '24px',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Category Badge */}
                  {/* <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    backgroundColor: '#2c5aa0',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                  }}>
                    {faq.infoType}
                  </div> */}

                  {/* Main Image */}
                  {faq.mainimage && (
                    <div style={{ marginBottom: '16px' }}>
                      <img 
                        src={faq.mainimage}
                        alt={faq.infoName}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                {/* Title */}
                <h3 style={{ 
                  color: '#2c5aa0', 
                  marginBottom: '12px',
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  lineHeight: '1.4',
                  marginTop: faq.mainimage ? '0' : '30px',
                  textAlign: 'left'
                }}>
                  {faq.infoName}
                </h3>
                
                {/* Header/Subtitle */}
                <h4 style={{
                  color: '#444',
                  marginBottom: '12px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  lineHeight: '1.4',
                  textAlign: 'left'
                }}>
                  {faq.infoHeader}
                </h4>
                
                {/* Content Preview */}
                <p style={{ 
                  color: '#666', 
                  lineHeight: '1.6',
                  margin: '0 0 16px 0',
                  fontSize: '0.95rem',
                  textAlign: 'left'
                }}>
                  {truncateText(faq.inforDetails)}
                </p>                  {/* Tags */}
                  {faq.tags && (
                    <div style={{ marginBottom: '16px' }}>
                      {faq.tags.split(',').slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          style={{
                            display: 'inline-block',
                            backgroundColor: '#f0f0f0',
                            color: '#666',
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            marginRight: '6px',
                            marginBottom: '6px'
                          }}
                        >
                          #{tag.trim()}
                        </span>
                      ))}
                      {faq.tags.split(',').length > 3 && (
                        <span style={{ fontSize: '0.8rem', color: '#999' }}>
                          +{faq.tags.split(',').length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Date */}
                  <div style={{
                    color: '#999',
                    fontSize: '0.85rem',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '12px'
                  }}>
                    {new Date(faq.createdDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // List View
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '16px'
            }}>
              {filteredFAQs.map((faq) => (
                <div 
                  key={faq.id}
                  onClick={() => handleCardClick(faq)}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'flex-start'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Image Section */}
                  {faq.mainimage && (
                    <div style={{ flexShrink: 0 }}>
                      <img 
                        src={faq.mainimage}
                        alt={faq.infoName}
                        style={{
                          width: '120px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Content Section */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                      {/* Title */}
                      <h3 style={{ 
                        color: '#2c5aa0', 
                        margin: '0 16px 0 0',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        lineHeight: '1.4',
                        flex: 1,
                        textAlign: 'left'
                      }}>
                        {faq.infoName}
                      </h3>
                      
                      {/* Category Badge */}
                      {/* <div style={{
                        backgroundColor: '#2c5aa0',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        flexShrink: 0
                      }}>
                        {faq.infoType}
                      </div> */}
                    </div>
                    
                    {/* Header/Subtitle */}
                    <h4 style={{
                      color: '#444',
                      margin: '0 0 8px 0',
                      fontSize: '1rem',
                      fontWeight: '500',
                      lineHeight: '1.4',
                      textAlign: 'left'
                    }}>
                      {faq.infoHeader}
                    </h4>
                    
                    {/* Content Preview */}
                    <p style={{ 
                      color: '#666', 
                      lineHeight: '1.5',
                      margin: '0 0 12px 0',
                      fontSize: '0.9rem',
                      textAlign: 'left'
                    }}>
                      {truncateText(faq.inforDetails, 150)}
                    </p>

                    {/* Footer with Tags and Date */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {/* Tags */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {faq.tags && faq.tags.split(',').slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            style={{
                              backgroundColor: '#f0f0f0',
                              color: '#666',
                              padding: '2px 8px',
                              borderRadius: '8px',
                              fontSize: '0.75rem'
                            }}
                          >
                            #{tag.trim()}
                          </span>
                        ))}
                        {faq.tags && faq.tags.split(',').length > 2 && (
                          <span style={{ fontSize: '0.75rem', color: '#999' }}>
                            +{faq.tags.split(',').length - 2} more
                          </span>
                        )}
                      </div>

                      {/* Date */}
                      <div style={{
                        color: '#999',
                        fontSize: '0.8rem'
                      }}>
                        {new Date(faq.createdDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results Message */}
          {filteredFAQs.length === 0 && (
            <div style={{ 
              textAlign: 'left', 
              padding: '60px 20px',
              color: '#666',
              fontSize: '1.1rem'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔍</div>
              <div>No articles found matching your criteria.</div>
              <div style={{ fontSize: '0.9rem', marginTop: '10px' }}>
                Try adjusting your search terms or category filter.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FAQ;